import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { publicSignInApi } from '@/api/signin'
import { Qr, signInUrl } from './Qr'

/**
 * The sign-in QR code on a page of its own, for a projector or a shared window, so the trainer doesn't have to put
 * the CertSlate register on screen. It shows only the code, the link and the course name: no names, no counts.
 * It reads the same public information as the sign-in page and notices when the window closes.
 */
export default function SignInDisplayPage() {
  const { code = '' } = useParams<{ code: string }>()
  const info = useQuery({
    queryKey: ['public-signin', code],
    queryFn: () => publicSignInApi.info(code),
    retry: false,
    refetchInterval: 10_000,
  })

  // The QR fills most of the screen's shorter side, and follows the window if it is resized.
  const [size, setSize] = useState(() => Math.min(window.innerWidth * 0.8, window.innerHeight * 0.62))
  useEffect(() => {
    const onResize = () => setSize(Math.min(window.innerWidth * 0.8, window.innerHeight * 0.62))
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const colour = info.data?.primaryColour ?? '#4F46E5'
  const url = signInUrl(code)
  const open = info.data?.open === true

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="px-6 py-4 flex items-center gap-4" style={{ backgroundColor: colour }}>
        {info.data?.logoUrl && <img src={info.data.logoUrl} alt="" className="h-10 w-auto object-contain" />}
        <span className="text-white font-bold text-2xl leading-tight">{info.data?.organisation ?? 'Sign in'}</span>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center gap-5 px-6 py-6 text-center">
        {info.isError && <p className="text-2xl text-gray-600">This sign-in link isn&apos;t valid.</p>}

        {info.data && !open && (
          <>
            <h1 className="text-3xl font-semibold text-gray-900">Sign-in has closed</h1>
            <p className="text-lg text-gray-500">If you&apos;re at the course, let your trainer know.</p>
          </>
        )}

        {info.data && open && (
          <>
            <h1 className="text-3xl sm:text-4xl font-semibold text-gray-900">Scan to sign in</h1>
            {info.data.title && <p className="text-xl text-gray-500 -mt-3">{info.data.title}</p>}
            <div className="rounded-2xl border-4 p-3 bg-white" style={{ borderColor: colour }}>
              <Qr url={url} size={Math.max(160, Math.round(size))} />
            </div>
            <p className="text-lg sm:text-xl text-gray-700 break-all">{url.replace(/^https?:\/\//, '')}</p>
          </>
        )}
      </main>
    </div>
  )
}
